import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { HandoffCategorySchema } from "../agents/support-agents/handoff-categories";
import { SupportAgentToolName } from "../agents/support-agents/support-agent-tool-names";
import { ConversationPlatform } from "../conversation";
import {
  ConversationAssigneeSummarySchema,
  ConversationCategorySchema,
  ConversationResolutionSentimentSchema,
  SupportActionSchema,
} from "../conversation-insights";
import { ConversationTableSortSchema } from "../tables/conversations-table-sort";
import {
  ChannelFilterType,
  ConversationKnownDateFilterTimeFrame,
  CustomDateSchema,
  DateFilterQueryType,
  FiltersStatusSchema,
  PendingFilter,
} from "./conversation-filters";

export const CONVERSATIONS_INSIGHTS_FILTERS_VERSION = "1";

export enum FilterGroupInsightsFilterOption {
  CHANNELS = "channels",
  CREATED_DATE = "createdDate",
  CLOSED_DATE = "closedDate",
  LAST_RESPONSE_AT = "lastResponseAt",
  CONVERSATION_INSIGHTS_SUPPORT_ACTIONS = "conversationInsightsSupportActions",
  CONVERSATION_INSIGHTS_CATEGORIES = "conversationInsightsCategories",
  CONVERSATION_INSIGHTS_SENTIMENT = "conversationInsightsSentiment",
  CONVERSATION_INSIGHTS_ASSIGNEE_SUMMARY = "conversationInsightsAssigneeSummary",
  CONVERSATION_INSIGHTS_AI_CATEGORIES = "conversationInsightsAiCategories",
  CONVERSATION_INSIGHTS_TOOL_NAMES = "conversationInsightsToolNames",
  CONVERSATION_INSIGHTS_TOOL_METRICS = "conversationInsightsToolMetrics",
  CONVERSATION_INSIGHTS_TOOL_ERROR_TYPES = "conversationInsightsToolErrorTypes",
  CONVERSATION_INSIGHTS_HANDOFF_CATEGORY = "conversationInsightsHandoffCategory",
  CONVERSATION_INSIGHTS_HANDOFF_SOURCE = "conversationInsightsHandoffSource",
  CONVERSATION_INSIGHTS_HANDOFF_SUGGESTED_TOOL = "conversationInsightsHandoffSuggestedTool",
  CONVERSATION_INSIGHTS_TAGS = "conversationInsightsTags",
  CONVERSATION_INSIGHTS_ORDER_METRICS = "conversationInsightsOrderMetrics",
}

export enum ConversationInsightsSupportActionsFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsCategoriesFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsSentimentFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsAssigneeSummaryFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsAiCategoriesFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsToolNamesFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsToolMetricsFilterType {
  TOTAL_CALLS_GREATER_THAN = "totalCallsGreaterThan",
  TOTAL_CALLS_LESS_THAN = "totalCallsLessThan",
  TOTAL_CALLS_EQUAL_TO = "totalCallsEqualTo",
  SUCCESS_COUNT_GREATER_THAN = "successCountGreaterThan",
  SUCCESS_COUNT_LESS_THAN = "successCountLessThan",
  SUCCESS_COUNT_EQUAL_TO = "successCountEqualTo",
  ERROR_COUNT_GREATER_THAN = "errorCountGreaterThan",
  ERROR_COUNT_LESS_THAN = "errorCountLessThan",
  ERROR_COUNT_EQUAL_TO = "errorCountEqualTo",
}

export enum ConversationInsightsToolErrorTypesFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsHandoffCategoryFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsHandoffSourceFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsHandoffSuggestedToolFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
  ENABLED = "enabled",
  EXISTS = "exists",
}

export enum ConversationInsightsTagsFilterType {
  INCLUDES = "includes",
  EXCLUDES = "excludes",
}

export enum ConversationInsightsOrderMetricsFilterType {
  NUMBER_OF_ORDERS_GREATER_THAN = "numberOfOrdersGreaterThan",
  NUMBER_OF_ORDERS_LESS_THAN = "numberOfOrdersLessThan",
  NUMBER_OF_ORDERS_EQUAL_TO = "numberOfOrdersEqualTo",
  ORDER_VALUE_GREATER_THAN = "orderValueGreaterThan",
  ORDER_VALUE_LESS_THAN = "orderValueLessThan",
  ORDER_VALUE_EQUAL_TO = "orderValueEqualTo",
}

// Zod schemas for validation
export const FilterGroupInsightsFilterOptionSchema = createEnum(
  FilterGroupInsightsFilterOption,
);
export const ConversationPlatformSchema = createEnum(ConversationPlatform);
export const ChannelFilterTypeSchema = createEnum(ChannelFilterType);
export const DateFilterQueryTypeSchema = createEnum(DateFilterQueryType);
export const KnownDateFilterTimeFrameSchema = createEnum(
  ConversationKnownDateFilterTimeFrame,
);
export const ConversationInsightsSupportActionsFilterTypeSchema = createEnum(
  ConversationInsightsSupportActionsFilterType,
);
export const ConversationInsightsCategoriesFilterTypeSchema = createEnum(
  ConversationInsightsCategoriesFilterType,
);
export const ConversationInsightsSentimentFilterTypeSchema = createEnum(
  ConversationInsightsSentimentFilterType,
);
export const ConversationInsightsAssigneeSummaryFilterTypeSchema = createEnum(
  ConversationInsightsAssigneeSummaryFilterType,
);
export const ConversationInsightsAiCategoriesFilterTypeSchema = createEnum(
  ConversationInsightsAiCategoriesFilterType,
);
export const ConversationInsightsToolNamesFilterTypeSchema = createEnum(
  ConversationInsightsToolNamesFilterType,
);
export const ConversationInsightsToolMetricsFilterTypeSchema = createEnum(
  ConversationInsightsToolMetricsFilterType,
);
export const ConversationInsightsToolErrorTypesFilterTypeSchema = createEnum(
  ConversationInsightsToolErrorTypesFilterType,
);
export const ConversationInsightsHandoffCategoryFilterTypeSchema = createEnum(
  ConversationInsightsHandoffCategoryFilterType,
);
export const ConversationInsightsHandoffSourceFilterTypeSchema = createEnum(
  ConversationInsightsHandoffSourceFilterType,
);
export const ConversationInsightsHandoffSuggestedToolFilterTypeSchema =
  createEnum(ConversationInsightsHandoffSuggestedToolFilterType);
export const ConversationInsightsTagsFilterTypeSchema = createEnum(
  ConversationInsightsTagsFilterType,
);
export const ConversationInsightsOrderMetricsFilterTypeSchema = createEnum(
  ConversationInsightsOrderMetricsFilterType,
);

// Individual filter schemas
export const ChannelsInsightsFilterSchema = z.object({
  type: z.literal(FilterGroupInsightsFilterOption.CHANNELS),
  value: z.array(ConversationPlatformSchema),
  query: ChannelFilterTypeSchema,
});

export const CreatedDateInsightsFilterSchema = z.object({
  type: z.literal(FilterGroupInsightsFilterOption.CREATED_DATE),
  value: KnownDateFilterTimeFrameSchema,
  query: DateFilterQueryTypeSchema,
  customDate: CustomDateSchema.optional(),
});

export const LastResponseAtInsightsFilterSchema = z.object({
  type: z.literal(FilterGroupInsightsFilterOption.LAST_RESPONSE_AT),
  value: KnownDateFilterTimeFrameSchema,
  query: DateFilterQueryTypeSchema,
  customDate: CustomDateSchema.optional(),
});

export const ClosedDateInsightsFilterSchema = z.object({
  type: z.literal(FilterGroupInsightsFilterOption.CLOSED_DATE),
  value: KnownDateFilterTimeFrameSchema,
  query: DateFilterQueryTypeSchema,
  customDate: CustomDateSchema.optional(),
});

export const ConversationInsightsSupportActionsFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_SUPPORT_ACTIONS,
  ),
  value: z.array(SupportActionSchema),
  query: ConversationInsightsSupportActionsFilterTypeSchema,
});

export const ConversationInsightsCategoriesFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_CATEGORIES,
  ),
  value: z.array(ConversationCategorySchema),
  query: ConversationInsightsCategoriesFilterTypeSchema,
});

export const ConversationInsightsSentimentFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_SENTIMENT,
  ),
  value: z.array(ConversationResolutionSentimentSchema),
  query: ConversationInsightsSentimentFilterTypeSchema,
});

export const ConversationInsightsAssigneeSummaryFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_ASSIGNEE_SUMMARY,
  ),
  value: z.array(ConversationAssigneeSummarySchema),
  query: ConversationInsightsAssigneeSummaryFilterTypeSchema,
});

export const ConversationInsightsAiCategoriesFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_AI_CATEGORIES,
  ),
  value: z.array(z.string()),
  query: ConversationInsightsAiCategoriesFilterTypeSchema,
});

export const ConversationInsightsToolNamesFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_TOOL_NAMES,
  ),
  value: z.array(z.string()),
  query: ConversationInsightsToolNamesFilterTypeSchema,
});

export const ConversationInsightsToolMetricsFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_TOOL_METRICS,
  ),
  value: z.number(), // Only numeric values for metrics
  query: ConversationInsightsToolMetricsFilterTypeSchema,
});

export const ConversationInsightsToolErrorTypesFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_TOOL_ERROR_TYPES,
  ),
  value: z.array(z.string()), // Array of error type strings
  query: ConversationInsightsToolErrorTypesFilterTypeSchema,
});

export const ConversationInsightsHandoffCategoryFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_HANDOFF_CATEGORY,
  ),
  value: z.array(HandoffCategorySchema),
  query: ConversationInsightsHandoffCategoryFilterTypeSchema,
});

export const ConversationInsightsHandoffSourceFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_HANDOFF_SOURCE,
  ),
  value: z.array(z.enum(["tool-call", "handoff-detection"])), // Array of handoff source enum values
  query: ConversationInsightsHandoffSourceFilterTypeSchema,
});

export const ConversationInsightsHandoffSuggestedToolFilterSchema =
  z.discriminatedUnion("query", [
    z.object({
      type: z.literal(
        FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_HANDOFF_SUGGESTED_TOOL,
      ),
      query: z.literal(
        ConversationInsightsHandoffSuggestedToolFilterType.INCLUDES,
      ),
      value: z.array(createEnum(SupportAgentToolName)),
    }),
    z.object({
      type: z.literal(
        FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_HANDOFF_SUGGESTED_TOOL,
      ),
      query: z.literal(
        ConversationInsightsHandoffSuggestedToolFilterType.EXCLUDES,
      ),
      value: z.array(createEnum(SupportAgentToolName)),
    }),
    z.object({
      type: z.literal(
        FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_HANDOFF_SUGGESTED_TOOL,
      ),
      query: z.literal(
        ConversationInsightsHandoffSuggestedToolFilterType.ENABLED,
      ),
      value: z.boolean(),
    }),
    z.object({
      type: z.literal(
        FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_HANDOFF_SUGGESTED_TOOL,
      ),
      query: z.literal(
        ConversationInsightsHandoffSuggestedToolFilterType.EXISTS,
      ),
      value: z.boolean(),
    }),
  ]);

export const ConversationInsightsTagsFilterSchema = z.object({
  type: z.literal(FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_TAGS),
  value: z.array(z.string()),
  query: ConversationInsightsTagsFilterTypeSchema,
});

export const ConversationInsightsOrderMetricsFilterSchema = z.object({
  type: z.literal(
    FilterGroupInsightsFilterOption.CONVERSATION_INSIGHTS_ORDER_METRICS,
  ),
  value: z.number(), // Numeric value for order count or order value comparison
  query: ConversationInsightsOrderMetricsFilterTypeSchema,
});

const AdvancedInsightsFilterSchema_ = z.discriminatedUnion("type", [
  ChannelsInsightsFilterSchema,
  CreatedDateInsightsFilterSchema,
  LastResponseAtInsightsFilterSchema,
  ClosedDateInsightsFilterSchema,
  ConversationInsightsSupportActionsFilterSchema,
  ConversationInsightsCategoriesFilterSchema,
  ConversationInsightsSentimentFilterSchema,
  ConversationInsightsAssigneeSummaryFilterSchema,
  ConversationInsightsAiCategoriesFilterSchema,
  ConversationInsightsToolNamesFilterSchema,
  ConversationInsightsToolMetricsFilterSchema,
  ConversationInsightsToolErrorTypesFilterSchema,
  ConversationInsightsHandoffCategoryFilterSchema,
  ConversationInsightsHandoffSourceFilterSchema,
  ConversationInsightsHandoffSuggestedToolFilterSchema,
  ConversationInsightsTagsFilterSchema,
  ConversationInsightsOrderMetricsFilterSchema,
]);
export const AdvancedInsightsFilterSchema: z.ZodType<AdvancedInsightsFilter> =
  AdvancedInsightsFilterSchema_;
export type AdvancedInsightsFilter = {} & z.infer<
  typeof AdvancedInsightsFilterSchema_
>;

export const UniqueInsightsV1FiltersSchema = z.object({
  status: FiltersStatusSchema.optional().nullable(),
  sort: ConversationTableSortSchema.optional(),
});

export const ConversationInsightsFiltersV1Schema =
  UniqueInsightsV1FiltersSchema.extend({
    advancedFilters: z.array(AdvancedInsightsFilterSchema),
  });

// Type exports generated from schemas (complex types only - enums are already exported above)
export type ChannelsInsightsFilter = z.infer<
  typeof ChannelsInsightsFilterSchema
>;
export type CreatedDateInsightsFilter = z.infer<
  typeof CreatedDateInsightsFilterSchema
>;
export type LastResponseAtInsightsFilter = z.infer<
  typeof LastResponseAtInsightsFilterSchema
>;
export type ClosedDateInsightsFilter = z.infer<
  typeof ClosedDateInsightsFilterSchema
>;
export type ConversationInsightsSupportActionsFilter = z.infer<
  typeof ConversationInsightsSupportActionsFilterSchema
>;
export type ConversationInsightsCategoriesFilter = z.infer<
  typeof ConversationInsightsCategoriesFilterSchema
>;
export type ConversationInsightsSentimentFilter = z.infer<
  typeof ConversationInsightsSentimentFilterSchema
>;
export type ConversationInsightsAssigneeSummaryFilter = z.infer<
  typeof ConversationInsightsAssigneeSummaryFilterSchema
>;
export type ConversationInsightsAiCategoriesFilter = z.infer<
  typeof ConversationInsightsAiCategoriesFilterSchema
>;
export type ConversationInsightsToolNamesFilter = z.infer<
  typeof ConversationInsightsToolNamesFilterSchema
>;
export type ConversationInsightsToolMetricsFilter = z.infer<
  typeof ConversationInsightsToolMetricsFilterSchema
>;
export type ConversationInsightsToolErrorTypesFilter = z.infer<
  typeof ConversationInsightsToolErrorTypesFilterSchema
>;
export type ConversationInsightsHandoffCategoryFilter = z.infer<
  typeof ConversationInsightsHandoffCategoryFilterSchema
>;
export type ConversationInsightsHandoffSourceFilter = z.infer<
  typeof ConversationInsightsHandoffSourceFilterSchema
>;
export type ConversationInsightsHandoffSuggestedToolFilter = z.infer<
  typeof ConversationInsightsHandoffSuggestedToolFilterSchema
>;
export type ConversationInsightsTagsFilter = z.infer<
  typeof ConversationInsightsTagsFilterSchema
>;
export type ConversationInsightsOrderMetricsFilter = z.infer<
  typeof ConversationInsightsOrderMetricsFilterSchema
>;

export type UniqueInsightsV1Filters = z.infer<
  typeof UniqueInsightsV1FiltersSchema
>;
export type ConversationInsightsFiltersV1 = z.infer<
  typeof ConversationInsightsFiltersV1Schema
>;

export type PendingChannelsInsightsFilter =
  PendingFilter<ChannelsInsightsFilter>;
export type PendingCreatedDateInsightsFilter =
  PendingFilter<CreatedDateInsightsFilter>;
export type PendingLastResponseAtInsightsFilter =
  PendingFilter<LastResponseAtInsightsFilter>;
export type PendingClosedDateInsightsFilter =
  PendingFilter<ClosedDateInsightsFilter>;
export type PendingConversationInsightsSupportActionsFilter =
  PendingFilter<ConversationInsightsSupportActionsFilter>;
export type PendingConversationInsightsCategoriesFilter =
  PendingFilter<ConversationInsightsCategoriesFilter>;
export type PendingConversationInsightsSentimentFilter =
  PendingFilter<ConversationInsightsSentimentFilter>;
export type PendingConversationInsightsAssigneeSummaryFilter =
  PendingFilter<ConversationInsightsAssigneeSummaryFilter>;
export type PendingConversationInsightsAiCategoriesFilter =
  PendingFilter<ConversationInsightsAiCategoriesFilter>;
export type PendingConversationInsightsToolNamesFilter =
  PendingFilter<ConversationInsightsToolNamesFilter>;
export type PendingConversationInsightsToolMetricsFilter =
  PendingFilter<ConversationInsightsToolMetricsFilter>;
export type PendingConversationInsightsToolErrorTypesFilter =
  PendingFilter<ConversationInsightsToolErrorTypesFilter>;
export type PendingConversationInsightsHandoffCategoryFilter =
  PendingFilter<ConversationInsightsHandoffCategoryFilter>;
export type PendingConversationInsightsHandoffSourceFilter =
  PendingFilter<ConversationInsightsHandoffSourceFilter>;
export type PendingConversationInsightsHandoffSuggestedToolFilter =
  PendingFilter<ConversationInsightsHandoffSuggestedToolFilter>;
export type PendingConversationInsightsTagsFilter =
  PendingFilter<ConversationInsightsTagsFilter>;
export type PendingConversationInsightsOrderMetricsFilter =
  PendingFilter<ConversationInsightsOrderMetricsFilter>;

export type PendingAdvancedInsightsFilter =
  | PendingChannelsInsightsFilter
  | PendingCreatedDateInsightsFilter
  | PendingLastResponseAtInsightsFilter
  | PendingClosedDateInsightsFilter
  | PendingConversationInsightsSupportActionsFilter
  | PendingConversationInsightsCategoriesFilter
  | PendingConversationInsightsSentimentFilter
  | PendingConversationInsightsAssigneeSummaryFilter
  | PendingConversationInsightsAiCategoriesFilter
  | PendingConversationInsightsToolNamesFilter
  | PendingConversationInsightsToolMetricsFilter
  | PendingConversationInsightsToolErrorTypesFilter
  | PendingConversationInsightsHandoffCategoryFilter
  | PendingConversationInsightsHandoffSourceFilter
  | PendingConversationInsightsHandoffSuggestedToolFilter
  | PendingConversationInsightsTagsFilter
  | PendingConversationInsightsOrderMetricsFilter;
